import React, { useEffect } from 'react';
import { IconButton } from '../../IconButton';
import { getClassNames } from './TableRow.classNames';
import classnames from 'classnames';
import { t } from '../../utils';

/**
 * @deprecated Komponenten er erstattet av Table.Row fra "@skatteetaten/ds-table"
 *
 * visibleName TableRow (Tabellrad)
 */
export const TableRow = props => {
  const {
    rowIndex,
    customClassNames,
    data,
    editableRows,
    editableContent,
    columns,
    editModeActive,
    expandableContent,
    expandableModeActive,
    expandableRows,
    expandIconPlacement,
    tableHasScroll,
    isEditableRowOpen = false,
    isExpandableRowOpen = false,
    onCloseRow,
    onEditRow,
    onExpandRow,
    openExpandableRowIndex,
    tableId,
    openEditableOnRowClick,
    showRowSeparators,
    compactTable
  } = props;
  const showExpandCollapseButton = !data.hideExpand && expandableRows;
  const editableRow = !data.hideEdit && editableRows;
  const showExtraCol = data.hideEdit && editableRows;
  const showRowSeparator = !data.hideSeparator && showRowSeparators;
  const numberOfColumns = columns.length + (editableRow || expandableRows || showExtraCol ? 1 : 0);
  const expandabledRowRef = React.useRef(null);
  const expandCollapseCellRef = /*#__PURE__*/React.createRef();
  const [focusRow, setFocusRow] = React.useState(openExpandableRowIndex);
  useEffect(() => {
    if (openExpandableRowIndex !== undefined) {
      setFocusRow(openExpandableRowIndex);
    }
  }, [openExpandableRowIndex]);
  useEffect(() => {
    if (focusRow === rowIndex && expandCollapseCellRef.current) {
      const knapp = expandCollapseCellRef.current.children[0];
      knapp.focus();
    }
  }, [expandCollapseCellRef, focusRow, rowIndex]);
  const styles = getClassNames(props, expandabledRowRef?.current?.offsetWidth);
  const childrenLength = !!data['children'] ? data['children'].length : 0;
  const editButton = /*#__PURE__*/React.createElement("span", {
    className: classnames(styles.cellContent, customClassNames?.cellContent, data.customClassNames?.cellContent, {
      cellContentSmall: !showRowSeparators || compactTable
    })
  }, /*#__PURE__*/React.createElement(IconButton, {
    title: t('tablerow.editable.title'),
    type: "button",
    icon: "Edit",
    className: styles.editButton,
    buttonSize: compactTable ? 'xSmall' : 'default',
    onClick: () => onEditRow(rowIndex),
    disabled: editModeActive || expandableModeActive,
    "aria-describedby": tableId.concat(rowIndex.toString(), '_0')
  }));
  const expandableContentId = `${tableId}-${rowIndex}-expanded`;

  const expandableCellContent = () => {
    if (expandableContent) {
      return /*#__PURE__*/React.createElement("div", {
        id: expandableContentId,
        className: classnames(styles.expandableContent, customClassNames?.expandableContent, data?.customClassNames?.expandableContent)
      }, expandableContent(data, onCloseRow, rowIndex));
    }

    return undefined;
  };

  const ExpandCollapseButton = btnProps => {
    return /*#__PURE__*/React.createElement("td", {
      "data-testid": 'table-cell-expandable',
      ref: expandCollapseCellRef,
      className: classnames(styles.tableCell, customClassNames?.tableCell, data.customClassNames?.tableCell, 'tableCellForExpandCollapseButton')
    }, /*#__PURE__*/React.createElement(IconButton, {
      id: `${tableId}-${rowIndex}-expand-button`,
      title: t('tablerow.expandable.title'),
      className: classnames(styles.expandButton, {
        [styles.expandButtonIsActive]: isExpandableRowOpen
      }),
      icon: btnProps.isOpen ? 'ChevronUp' : 'ChevronDown',
      onClick: () => {
        if (btnProps.isOpen) {
          onCloseRow();
        } else {
          onExpandRow(rowIndex);
        }
      },
      buttonSize: compactTable ? 'xSmall' : 'large',
      type: "button",
      "aria-expanded": btnProps.isOpen,
      "aria-describedby": tableId.concat(rowIndex.toString(), '_0'),
      "aria-controls": btnProps.isOpen ? expandableContentId : undefined,
      disabled: editModeActive
    }), btnProps.isOpen && btnProps.shouldRenderCellContent && expandableCellContent());
  };

  const actionButtons = editableRow || expandableRows ? /*#__PURE__*/React.createElement(React.Fragment, null, editableRow && /*#__PURE__*/React.createElement("td", {
    className: classnames(styles.tableCell, customClassNames?.tableCell, data.customClassNames?.tableCell, {
      tableCellHasSeparator: showRowSeparator && !isExpandableRowOpen
    }),
    rowSpan: childrenLength + 1
  }, editButton), showExpandCollapseButton && /*#__PURE__*/React.createElement(ExpandCollapseButton, {
    isOpen: isExpandableRowOpen,
    shouldRenderCellContent: expandIconPlacement === 'before'
  })) : null;

  const renderCellContent = (content, index, alignment, isChild, isExpandabledRowOpen) => openEditableOnRowClick && editableContent && editableRow ? /*#__PURE__*/React.createElement("button", {
    "data-testid": 'openEditableOnRowClick-button',
    className: styles.editButton,
    onClick: () => onEditRow(index),
    tabIndex: -1
  }, /*#__PURE__*/React.createElement("span", {
    className: classnames(styles.cellContent, customClassNames?.cellContent, data.customClassNames?.cellContent, 'cellContentClickable', {
      cellContentAboveExpandedArea: isExpandabledRowOpen,
      cellContentAlignedRight: alignment === 'right',
      cellContentAlignedCenter: alignment === 'center',
      cellContentLarge: showRowSeparator && !isChild,
      cellContentChildRow: isChild,
      cellContentHideEdit: !editableRow && editableRows
    })
  }, formatContent(content, index))) : /*#__PURE__*/React.createElement("div", {
    className: classnames(styles.cellContent, customClassNames?.cellContent, data.customClassNames?.cellContent, {
      cellContentAboveExpandedArea: isExpandabledRowOpen,
      cellContentAlignedRight: alignment === 'right',
      cellContentAlignedCenter: alignment === 'center',
      cellContentLarge: showRowSeparator && !isChild,
      cellContentChildRow: isChild,
      cellContentHideEdit: !editableRow && editableRows
    })
  }, formatContent(content, index));

  const formatContent = (content, index) => {
    if (columns[index].formatFunction) {
      return columns[index].formatFunction(content);
    }

    return content;
  };

  const renderRow = (rowData, rowColumns, rowKey, isRowExpanded, isChild = false) => {
    return rowColumns.map((column, cellIndex) => {
      if (cellIndex > 0) {
        return /*#__PURE__*/React.createElement("td", {
          key: tableId.concat(rowKey.toString(), '_', cellIndex.toString()),
          className: classnames(styles.tableCell, customClassNames?.tableCell, data.customClassNames?.tableCell, {
            tableCellAboveExpandedArea: isRowExpanded,
            tableCellAlignedRight: column.alignment === 'right',
            tableCellAlignedCenter: column.alignment === 'center',
            tableCellIsEditableRowClosed: editableRow && !props.isEditableRowOpen,
            tableCellHiddenOnMobile: column.hideOnMobile,
            tableCellHasSeparator: showRowSeparator && !isExpandableRowOpen && !data['children']
          })
        }, renderCellContent(rowData[column.fieldName], cellIndex, column.alignment, isChild, isRowExpanded));
      } else if (!isChild) {
        return /*#__PURE__*/React.createElement("th", {
          key: tableId.concat(rowKey.toString(), '_', cellIndex.toString()),
          id: tableId.concat(rowKey.toString(), '_', cellIndex.toString()),
          className: classnames(styles.tableCell, customClassNames?.tableCell, data.customClassNames?.tableCell, {
            tableCellAboveExpandedArea: isRowExpanded,
            tableCellAlignedRight: column.alignment === 'right',
            tableCellAlignedCenter: column.alignment === 'center',
            tableCellIsEditableRowClosed: editableRow && !props.isEditableRowOpen,
            tableCellHiddenOnMobile: column.hideOnMobile,
            tableCellHasSeparator: showRowSeparator && !isExpandableRowOpen
          }),
          scope: 'row',
          rowSpan: childrenLength + 1
        }, renderCellContent(rowData[column.fieldName], cellIndex, column.alignment, isChild, isRowExpanded));
      }

      return null;
    });
  };

  if (isEditableRowOpen) {
    return /*#__PURE__*/React.createElement("tr", {
      key: rowIndex,
      className: classnames(styles.tableRow, customClassNames?.tableRow, data.customClassNames?.tableRow, isEditableRowOpen ? 'tableRowEditableAndOpen' : 'tableRowEditableAndClosed', 'tableRowHasSeparator')
    }, /*#__PURE__*/React.createElement("td", {
      key: rowIndex,
      "data-testid": 'editable-content',
      className: classnames(styles.tableCell, customClassNames?.tableCell, data.customClassNames?.tableCell),
      colSpan: numberOfColumns
    }, editableContent && editableContent(data, onCloseRow, rowIndex)));
  }

  const actionButtonsBefore = tableHasScroll || expandIconPlacement === 'before';
  return /*#__PURE__*/React.createElement(React.Fragment, null, expandableRows ? isExpandableRowOpen ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("tr", {
    key: rowIndex,
    ref: expandabledRowRef,
    className: classnames(styles.tableRow, customClassNames?.tableRow, data.customClassNames?.tableRow, {
      tableRowHasSeparator: showRowSeparator && (expandIconPlacement === 'before' || expandIconPlacement === 'after' && !isExpandableRowOpen)
    })
  }, showExpandCollapseButton && expandIconPlacement === 'before' && /*#__PURE__*/React.createElement(ExpandCollapseButton, {
    isOpen: true,
    shouldRenderCellContent: true
  }), renderRow(data, columns, rowIndex, true), showExpandCollapseButton && expandIconPlacement !== 'before' && /*#__PURE__*/React.createElement(ExpandCollapseButton, {
    isOpen: true,
    shouldRenderCellContent: false
  })), expandIconPlacement !== 'before' && /*#__PURE__*/React.createElement("tr", {
    key: rowIndex + 'expanded',
    className: classnames(styles.tableRow, customClassNames?.tableRow, data.customClassNames?.tableRow, {
      tableRowHasSeparator: showRowSeparator
    })
  }, /*#__PURE__*/React.createElement("td", {
    colSpan: numberOfColumns
  }, expandableCellContent()))) : /*#__PURE__*/React.createElement("tr", {
    key: rowIndex,
    ref: expandabledRowRef,
    className: classnames(styles.tableRow, customClassNames?.tableRow, data.customClassNames?.tableRow, {
      tableRowIsClickable: openEditableOnRowClick,
      tableRowHasSeparator: showRowSeparator && !isExpandableRowOpen && !data['children']
    })
  }, (tableHasScroll || expandIconPlacement === 'before') && actionButtons, renderRow(data, columns, rowIndex, false), !tableHasScroll && expandIconPlacement !== 'before' && actionButtons) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("tr", {
    key: rowIndex,
    className: classnames(styles.tableRow, customClassNames?.tableRow, data.customClassNames?.tableRow, {
      tableRowIsClickable: openEditableOnRowClick,
      tableRowHasSeparator: showRowSeparator && !isExpandableRowOpen && !data['children']
    })
  }, actionButtonsBefore && actionButtons, renderRow(data, columns, rowIndex, false, false), !actionButtonsBefore && actionButtons, showExtraCol && /*#__PURE__*/React.createElement("td", null)), !isEditableRowOpen && !!data['children'] && childrenLength > 0 && data['children'].map((child, childIndex) => {
    return /*#__PURE__*/React.createElement("tr", {
      key: rowIndex + 'child' + childIndex,
      className: classnames(styles.tableRow, customClassNames?.tableRow, data.customClassNames?.tableRow, {
        tableRowHasSeparator: showRowSeparator && childIndex === data['children'].length - 1
      })
    }, actionButtonsBefore && /*#__PURE__*/React.createElement("td", null), renderRow(child, columns, childIndex, false, true), !actionButtonsBefore && /*#__PURE__*/React.createElement("td", null), showExtraCol && /*#__PURE__*/React.createElement("td", null));
  })));
};
export default TableRow;